import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Lock, BookOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WikiListPage = () => {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchEntries();
    fetchCategories();
  }, []);

  const fetchEntries = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wiki/entries`, {
        withCredentials: true
      });
      setEntries(data);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wiki/categories`, {
        withCredentials: true
      });
      setCategories(['All', ...data]);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filteredEntries = selectedCategory === 'All'
    ? entries
    : entries.filter(entry => entry.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center" data-testid="wiki-loading">
        <div className="text-primary text-xl font-serif">Loading glossary...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] py-12 px-6" data-testid="wiki-list-page">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h1 className="text-4xl lg:text-5xl font-serif tracking-tighter text-primary mb-4">Glossary</h1>
          <p className="text-base font-sans text-foreground/90 leading-relaxed max-w-2xl mx-auto">
            Explore the rich lore and history of the Growling Griffon and the world of Greyhawk
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 flex flex-wrap gap-2 justify-center" data-testid="category-filter">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
              className="rounded-sm font-sans"
              data-testid={`category-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Entries Grid */}
        {filteredEntries.length === 0 ? (
          <div className="text-center py-12" data-testid="no-entries-message">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
            <p className="text-lg font-sans text-muted-foreground">No glossary entries yet. Be the first to create one!</p>
            {user && (
              <Link to="/wiki/new" className="inline-block mt-4" data-testid="create-first-entry-link">
                <Button>Create Entry</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEntries.map((entry) => (
              <Link
                key={entry.id}
                to={`/wiki/${entry.id}`}
                className="block"
                data-testid={`wiki-entry-card-${entry.id}`}
              >
                <Card className="h-full bg-card border-border rounded-sm hover:border-primary/50 hover:shadow-[0_0_15px_rgba(245,158,11,0.15)] transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <CardTitle className="text-xl font-serif text-foreground line-clamp-2">
                        {entry.title}
                      </CardTitle>
                      {entry.is_members_only && (
                        <Lock className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="rounded-sm font-sans text-xs">
                        {entry.category}
                      </Badge>
                      {entry.is_members_only && !user && (
                        <Badge variant="outline" className="rounded-sm font-sans text-xs border-primary text-primary">
                          Members Only
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="font-sans text-foreground/80 line-clamp-3">
                      {entry.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                    </CardDescription>
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs font-sans text-muted-foreground">
                        By {entry.author_name} • {new Date(entry.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WikiListPage;
